import React, { useEffect, useRef, useState } from 'react';
import { Image, KeyboardAvoidingView, Platform, Pressable, ScrollView, StyleSheet, TextInput, View, type ViewStyle } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Check, Pencil, Search } from '../components/icons';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { ScreenHeader } from '../components/ScreenHeader';
import { CarteScore } from '../components/CarteScore';
import { CarteScoreInconnu } from '../components/CarteScoreInconnu';
import { BoutonPastille } from '../components/BoutonPastille';
import { PiedAction } from '../components/PiedAction';
import { GradientButton } from '../components/GradientButton';
import { CvPreview } from '../components/CvPreview';
import { CvEditor } from '../components/CvEditor';
import { TemplatePicker } from '../components/TemplatePicker';
import { DEFAULT_ACCENT, DEFAULT_TEMPLATE } from '../theme/templates';
import { serializeLanguages } from '../components/LanguagePicker';
import { serializeExperiences } from '../components/ExperienceEditor';
import { serializeProjects } from '../components/ProjectEditor';
import { serializeEducation } from '../components/EducationEditor';
import { CvCreateForm, ETAPES_CV, type CvFormValues } from '../components/CvCreateForm';
import { verdictScore } from './CvScoreScreen';
import { QualityCheckCard } from '../components/QualityCheckCard';
import { FeedbackWidget } from '../components/FeedbackWidget';
import { cvCreateAnswersKey, cvCreateSavedKey } from '../utils/resultRef';
import { useClaude } from '../hooks/useClaude';
import { useGabarit } from '../hooks/useGabarit';
import { useCvExport } from '../hooks/useCvExport';
import { saveGeneratedCv, updateCv } from '../services/cv';
import { rewriteSection, recommendCvForOffer, QuotaError } from '../services/claude';
import type { RewriteOutcome } from '../components/CvEditor';
import { getProfile, getAuthEmail } from '../services/profile';
import {
  lireDocumentImporte,
  pickDocumentFile,
  pickImages,
  type PickedFile,
  type PickedImage,
} from '../services/mediaPicker';
import { cvToPlainText } from '../utils/cvToPlainText';
import { colonneFormulaire, colors, formes, layout, radius, screenPadding, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';
import { proposeAbonnement, messageRefusSansOffre, prochaineRemiseAZero } from '../utils/quotaRefusal';
import { GenerationProgress } from '../components/GenerationProgress';
import type {
  CvContractType,
  CvTemplate,
  EducationEntry,
  ExperienceEntry,
  GeneratedCv,
  LanguageEntry,
  Profile,
  ProjectEntry,
  OutputLanguage,
} from '../types';

interface Question {
  key: string;
  label: string;
  placeholder: string;
  multiline?: boolean;
}

/**
 * Libellés envoyés DANS LE PROMPT, volontairement figés en français.
 *
 * `buildLabeled` préfixe chaque réponse de son libellé, et la chaîne obtenue part au modèle. La
 * traduire ferait dépendre le CONTENU DU PROMPT de la langue de l'INTERFACE : quelqu'un naviguant
 * en anglais enverrait un prompt différent pour le même CV. Les deux axes sont indépendants, et le
 * prompt appartient au document. La langue du CV produit se règle par outputLanguage, pas ici.
 */
const PROMPT_LABELS: Record<string, string> = {
  role: 'Poste visé',
  education: 'Formation, diplômes et certifications',
  experiences: 'Expériences professionnelles (stages, alternances, jobs)',
  projects: 'Projets personnels (perso, associatifs, side-projects)',
  skills: 'Compétences et outils',
  languages: 'Langues',
  interests: 'Centres d’intérêt et loisirs',
  extra: 'Quelque chose à ajouter ? (optionnel)',
  contrat: 'Type de contrat recherché',
};

// Valeur envoyée au modèle, FIGÉE en français comme les libellés ci-dessus (voir leur commentaire).
const PROMPT_CONTRATS: Record<CvContractType, string> = {
  alternance: 'alternance',
  stage: 'stage',
  cdi: 'CDI',
  cdd: 'CDD',
};

const QUESTION_KEYS: { key: string; multiline?: boolean; noPlaceholder?: boolean }[] = [
  { key: 'role' },
  { key: 'education', multiline: true },
  { key: 'experiences', multiline: true },
  { key: 'projects', multiline: true },
  { key: 'skills' },
  // Composant structuré (LanguagePicker) : aucun placeholder à afficher, donc aucune clé de
  // traduction vide à maintenir.
  { key: 'languages', noPlaceholder: true },
  { key: 'interests', multiline: true },
  { key: 'extra', multiline: true },
];

// Construit AU RENDU : une constante de module figerait les libellés dans la langue chargée au
// premier import, et le changement de langue ne les rattraperait jamais.
const questions = (): Question[] =>
  QUESTION_KEYS.map(({ key, noPlaceholder, ...rest }) => ({
    key,
    label: t(`cvCreate.questions.${key}.label`),
    placeholder: noPlaceholder ? '' : t(`cvCreate.questions.${key}.placeholder`),
    ...rest,
  }));




type Phase = 'form' | 'generating' | 'result';
type SaveState = 'idle' | 'saving' | 'saved' | 'error';

// Réouverture d'un CV sauvegardé : on hydrate l'écran et on démarre directement en phase 'result'.
export interface CvCreateInitial {
  cvId: string;
  cv: GeneratedCv;
  title: string;
  template: CvTemplate;
  accentColor: string | null;
  edit?: boolean; // ouvrir directement en mode édition (« Éditer ») plutôt qu'en lecture (« Aperçu »)
  /** « Optimiser mon CV » : une fiche de poste a été prise en compte (le résultat le dit). */
  ficheDePoste?: boolean;
}

export interface CvCreateOfferState {
  text?: string;
  file?: PickedFile | null;
  images?: PickedImage[];
}

export interface CvCreateScreenProps {
  initial?: CvCreateInitial;
  onBack?: () => void;
  // Signale à la couche navigation qu'une saisie ou un CV non enregistré serait perdu.
  onDirtyChange?: (dirty: boolean) => void;
  // « Enregistrer » a abouti : la couche navigation ramène à la liste des CV. Jamais appelé par
  // l'enregistrement automatique qui suit la génération, qui laisse sur l'aperçu pour l'export.
  onSaved?: () => void;
  // « Tester ce CV dans Note mon CV » : passe le CV généré (texte) + l'offre déjà fournie.
  onTestInScore?: (payload: { cvText: string; offer: CvCreateOfferState }) => void;
  // « Tu as déjà un CV ? Importe-le » : mène à l'import existant (Note mon CV, puis « Enregistrer »).
  // Absent, la carte n'est pas montrée.
  onImportCv?: () => void;
}

export function CvCreateScreen({
  initial,
  onBack,
  onTestInScore,
  onDirtyChange,
  onSaved,
  onImportCv,
}: CvCreateScreenProps = {}) {
  const { generateCv, loading, error } = useClaude();
  const { exportCv, exporting, error: exportError, ajustement } = useCvExport();
  const { large } = useGabarit();
  const [step, setStep] = useState(0);
  // Langue du CV PRODUIT. Aucune langue d'entrée n'est demandée : le modèle détecte celle des
  // réponses, et la consigne de prompt lui interdit de la suivre.
  const [outputLanguage, setOutputLanguage] = useState<OutputLanguage>('fr');
  const [name, setName] = useState('');
  // « Tu cherches… » (facultatif). Préchoisi d'après l'objet de recherche du profil quand il est
  // précis (stage, alternance) ; « contrat » est trop large pour trancher entre CDI et CDD.
  const [contractType, setContractType] = useState<CvContractType | null>(null);
  const contratTouche = useRef(false);
  // Réouverture : on alimente `role` depuis le titre normalisé (headline) pour l'aperçu et l'export.
  const [answers, setAnswers] = useState<Record<string, string>>(
    initial ? { role: initial.cv.headline } : {},
  ); // role, extra (texte)
  const [experiences, setExperiences] = useState<ExperienceEntry[]>([]);
  const [projects, setProjects] = useState<ProjectEntry[]>([]);
  const [education, setEducation] = useState<EducationEntry[]>([]);
  const [skills, setSkills] = useState<string[]>([]);
  const [interests, setInterests] = useState<string[]>([]);
  const [languages, setLanguages] = useState<LanguageEntry[]>([]);
  const [phase, setPhase] = useState<Phase>(initial ? 'result' : 'form');

  // Synchronisation animation ↔ réponse réelle : on passe en `result` quand les deux sont prêts.
  const [pendingResult, setPendingResult] = useState<GeneratedCv | null>(null);

  const [generated, setGenerated] = useState<GeneratedCv | null>(initial?.cv ?? null);
  // Une page A4, toujours : quand l'export a dû condenser ou tronquer, l'aperçu prend le contenu
  // réellement exporté. Score, conseils et contrôle qualité restent ceux de la génération.
  useEffect(() => {
    if (!ajustement) return;
    const a = ajustement.cv;
    setGenerated((prec) =>
      prec
        ? {
            ...prec,
            headline: a.headline,
            experiences: a.experiences,
            projects: a.projects,
            education: a.education,
            skills: a.skills,
            languages: a.languages,
            interests: a.interests,
          }
        : prec,
    );
  }, [ajustement]);
  // Clé du résultat (feedback) : réponses du wizard à la génération, id du CV si rouvert.
  const [resultKey, setResultKey] = useState(initial ? cvCreateSavedKey(initial.cvId) : '');
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [cvId, setCvId] = useState<string | null>(initial?.cvId ?? null);
  const [profile, setProfile] = useState<Profile | null>(null);

  // Template visuel + couleur d'accent (sauvegardés avec le CV).
  const [template, setTemplate] = useState<CvTemplate>(initial?.template ?? DEFAULT_TEMPLATE);
  const [accentColor, setAccentColor] = useState<string>(initial?.accentColor ?? DEFAULT_ACCENT);

  // Édition manuelle inline du CV généré. Réouverture « Éditer » → on démarre en mode édition.
  const [mode, setMode] = useState<'read' | 'edit'>(initial?.edit ? 'edit' : 'read');
  const [draft, setDraft] = useState<GeneratedCv | null>(initial?.edit ? initial.cv : null);
  const [editState, setEditState] = useState<SaveState>('idle');
  const [editError, setEditError] = useState<string | null>(null);

  // Ce qui serait réellement perdu à la sortie. Un wizard vierge n'arme RIEN : pas d'alerte au
  // premier écran sur un formulaire vide.
  //  - form       : au moins une saisie
  //  - generating : appel en vol, quota déjà consommé
  //  - result      : CV produit mais dont l'enregistrement a ÉCHOUÉ (sinon il est en base)
  //  - edit        : modifications manuelles non enregistrées (draft change de référence à la 1re édition)
  const hasFormInput =
    name.trim().length > 0 ||
    Object.values(answers).some((v) => v?.trim()) ||
    experiences.length > 0 ||
    projects.length > 0 ||
    education.length > 0 ||
    skills.length > 0 ||
    interests.length > 0 ||
    languages.length > 0;

  const dirty =
    (phase === 'form' && hasFormInput) ||
    phase === 'generating' ||
    (phase === 'result' && saveState === 'error') ||
    (mode === 'edit' && draft !== null && draft !== generated);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Fiche de poste optionnelle (phase result) → recommandations APRÈS génération. Le CV n'est pas régénéré.
  const [offerText, setOfferText] = useState('');
  const [offerFile, setOfferFile] = useState<PickedFile | null>(null);
  const [offerImages, setOfferImages] = useState<PickedImage[]>([]);
  const [recommendations, setRecommendations] = useState<string[] | null>(null);
  const [recoLoading, setRecoLoading] = useState(false);
  const [recoError, setRecoError] = useState<string | null>(null);

  // Profil chargé pour alimenter l'en-tête du CV (nom + coordonnées). Optionnel : silencieux si échec.
  useEffect(() => {
    getProfile()
      .then(setProfile)
      .catch(() => {});
  }, []);

  // En-tête CV : nom du profil prioritaire, repli sur le champ « nom » de l'étape 0.
  const profileName = [profile?.first_name, profile?.last_name]
    .map((v) => v?.trim())
    .filter(Boolean)
    .join(' ');
  const cvName = profileName || profile?.full_name?.trim() || name;
  const cvContact = {
    email: profile?.email ?? getAuthEmail() ?? undefined,
    phone: profile?.phone ?? undefined,
    address: profile?.address ?? profile?.city ?? undefined,
    linkedin: profile?.linkedin_url ?? undefined,
  };

  useEffect(() => {
    if (contratTouche.current) return;
    const cible = profile?.search_target;
    if (cible === 'stage' || cible === 'alternance') setContractType(cible);
  }, [profile]);

  const QUESTIONS = questions();
  const canGenerate = (answers.role ?? '').trim().length > 0;

  const cvTitle = () => ((answers.role ?? '').trim() ? `CV ${answers.role.trim()}` : 'Mon CV');

  const persist = async (cv: GeneratedCv) => {
    setSaveState('saving');
    try {
      const id = await saveGeneratedCv(cv, cvTitle(), template, accentColor, outputLanguage);
      setCvId(id);
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  };

  // Changement de template/couleur : aperçu live + sauvegarde silencieuse (le CV existe déjà).
  const handleTemplateChange = (next: { template: CvTemplate; accentColor: string }) => {
    setTemplate(next.template);
    setAccentColor(next.accentColor);
    if (cvId && generated) {
      void updateCv(cvId, generated, next.template, next.accentColor).catch(() => {});
    }
  };

  const startEdit = () => {
    if (!generated) return;
    setDraft(generated);
    setEditState('idle');
    setEditError(null);
    setMode('edit');
  };

  const cancelEdit = () => {
    setDraft(null);
    setMode('read');
  };

  // Enregistre l'édition manuelle : update si le CV existe déjà, sinon insertion (cas save initial échoué).
  const saveEdits = async () => {
    if (!draft) return;
    setEditState('saving');
    setEditError(null);
    try {
      if (cvId) {
        await updateCv(cvId, draft, template, accentColor);
      } else {
        const id = await saveGeneratedCv(draft, cvTitle(), template, accentColor, outputLanguage);
        setCvId(id);
      }
      setGenerated(draft);
      setEditState('saved');
      setMode('read');
      onSaved?.();
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Enregistrement impossible.');
      setEditState('error');
    }
  };

  // Réécriture IA d'une entrée (appel direct → état par entrée géré dans CvEditor).
  const handleRewrite = async (section: string, content: string): Promise<RewriteOutcome> => {
    try {
      const result = await rewriteSection(section, content);
      return { ok: true, after: result.after };
    } catch (e) {
      if (e instanceof QuotaError) {
        // Le message disait « tu as atteint ta limite » à un compte gratuit qui n'a JAMAIS eu de
        // réécritures (`section_rewrite` vaut 0 en Free), et invitait à passer en « Premium », un
        // plan qui n'existe pas — ils s'appellent Accélérateur, Pro et École. Les deux refus
        // portent désormais leurs propres mots, les mêmes que ceux du Paywall.
        return {
          ok: false,
          quota: true,
          message: proposeAbonnement(e.plan)
            ? t(e.reason === 'not_included' ? 'paywall.notIncluded' : 'paywall.capReached', {
                date: prochaineRemiseAZero(),
              })
            : messageRefusSansOffre(e.reason),
        };
      }
      return { ok: false, message: e instanceof Error ? e.message : 'La réécriture a échoué.' };
    }
  };

  // ───────────── Fiche de poste (phase result) ─────────────
  const pickOfferFile = async () => {
    setRecoError(null);
    const outcome = await pickDocumentFile();
    if (outcome.canceled) return;
    if (outcome.error) return setRecoError(outcome.error);
    if (outcome.file) {
      setOfferImages([]);
      setOfferFile(outcome.file);
    }
  };

  const addOfferImages = async () => {
    setRecoError(null);
    const outcome = await pickImages();
    if (outcome.canceled) return;
    if (outcome.error) setRecoError(outcome.error);
    if (outcome.images) {
      setOfferFile(null);
      setOfferImages(outcome.images);
    }
  };

  // Payload offre (captures > fichier > texte). undefined si rien fourni.
  const buildOffer = async (): Promise<{ text?: string; pdf?: { data: string }; docx?: { data: string }; images?: { media_type: PickedImage['media_type']; data: string }[] } | undefined> => {
    if (offerImages.length > 0) return { images: offerImages.map(({ media_type, data }) => ({ media_type, data })) };
    if (offerFile) {
      const { payload, error } = await lireDocumentImporte(offerFile);
      if (!payload) throw new Error(error);
      return payload;
    }
    if (offerText.trim()) return { text: offerText.trim() };
    return undefined;
  };

  // Recommandations vis-à-vis de l'offre — APRÈS génération, sans modifier le CV. Gratuit (pas de quota).
  const getRecommendations = async () => {
    if (!generated) return;
    setRecoLoading(true);
    setRecoError(null);
    try {
      // SOUS le `try`, et non avant : `buildOffer` lit un fichier, et une lecture ratée
      // interrompait ce gestionnaire sans rien afficher.
      const offer = await buildOffer();
      if (!offer) return;
      const res = await recommendCvForOffer(cvToPlainText(generated), offer);
      setRecommendations(res.recommendations);
    } catch (e) {
      setRecoError(e instanceof Error ? e.message : 'Recommandations indisponibles, réessaie.');
    } finally {
      setRecoLoading(false);
    }
  };

  const testInScore = () => {
    if (!generated || !onTestInScore) return;
    onTestInScore({
      cvText: cvToPlainText(generated),
      offer: { text: offerText, file: offerFile, images: offerImages },
    });
  };

  const hasOffer = offerText.trim().length > 0 || offerFile !== null || offerImages.length > 0;

  /**
   * Bascule vers le résultat DÈS que la réponse arrive.
   *
   * L'ancienne version attendait aussi la fin d'une animation de 1,8 s : une réponse en 800 ms
   * était donc retenue jusqu'à 1,8 s. Un délai fabriqué, contraire au principe qu'on n'invente
   * jamais un temps d'attente.
   */
  useEffect(() => {
    if (phase === 'generating' && pendingResult) {
      setGenerated(pendingResult);
      setPhase('result');
      void persist(pendingResult);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, pendingResult]);

  // Sérialise chaque section structurée en texte étiqueté (avec dates réelles).
  const serializeFor = (key: string): string => {
    switch (key) {
      case 'experiences':
        return serializeExperiences(experiences, outputLanguage);
      case 'projects':
        return serializeProjects(projects, outputLanguage);
      case 'education':
        return serializeEducation(education, outputLanguage);
      case 'skills':
        return skills.join(', ');
      case 'interests':
        return interests.join(', ');
      case 'languages':
        return serializeLanguages(languages);
      default:
        return answers[key] ?? ''; // role, extra
    }
  };

  const buildLabeled = (): string[] => {
    const out: string[] = [];
    for (const q of QUESTIONS) {
      const value = serializeFor(q.key).trim();
      if (value) out.push(`${PROMPT_LABELS[q.key]} : ${value}`);
      // Juste après le poste : c'est un renseignement sur le poste visé, pas une réponse de plus.
      if (q.key === 'role' && value && contractType) {
        out.push(`${PROMPT_LABELS.contrat} : ${PROMPT_CONTRATS[contractType]}`);
      }
    }
    return out;
  };

  const generate = async () => {
    setPendingResult(null);
    setGenerated(null);
    setSaveState('idle');
    setPhase('generating');
    const labeled = buildLabeled();
    const cv = await generateCv(labeled, outputLanguage);
    if (!cv) {
      // erreur ou quota → le hook a renseigné error / quotaFeature ; retour au formulaire.
      setPhase('form');
      return;
    }
    // Clé dérivée des RÉPONSES : régénérer avec les mêmes réponses => même result_ref.
    setResultKey(cvCreateAnswersKey(labeled));
    setPendingResult(cv);
  };

  // ───────────── generating ─────────────
  if (phase === 'generating') {
    return (
      <SafeAreaView style={styles.genContainer} edges={['top', 'bottom']}>
        <View style={styles.genContent}>
          <GenerationProgress
            messageKey="elapsed"
            // Jalons RÉELS : « Reçu » se coche quand la réponse arrive, « Enregistré » quand
            // l'insertion aboutit. Aucun n'est piloté par une minuterie.
            milestones={[
              { key: 'received', label: t('progress.received'), done: pendingResult !== null },
              { key: 'saved', label: t('progress.saved'), done: saveState === 'saved' },
            ]}
            testID="cvc-progress"
          />
        </View>
      </SafeAreaView>
    );
  }

  // ───────────── result · édition manuelle ─────────────
  if (phase === 'result' && mode === 'edit' && draft) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <ScreenHeader
          variante="carte"
          title={t('cvCreateFlow.editTitle')}
          onBack={cancelEdit}
          testID="cvc-back"
          droite={
            <BoutonPastille
              label={t('cvCreateFlow.save')}
              loading={editState === 'saving'}
              onPress={saveEdits}
              testID="cvc-edit-save"
            />
          }
        />
        {/* Hors du défilement : l'échec se lit sans remonter en haut de la liste. */}
        {editState === 'error' ? (
          <Text testID="cvc-edit-error" accessibilityRole="alert" style={styles.erreurEdition}>
            {editError ?? t('cvCreateFlow.editSaveFailed')} {t('cvEditor.rewriteRetry')}
          </Text>
        ) : null}
        <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <ScrollView
            contentContainerStyle={[styles.content, colonneFormulaire]}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
          >
            <CvEditor value={draft} onChange={setDraft} onRewrite={handleRewrite} testID="cvc-editor" />
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    );
  }

  // ───────────── result · lecture ─────────────
  if (phase === 'result' && generated) {
    // Même statut sous la carte de score et sous celle d'un CV non noté.
    const statutEnregistrement =
      saveState === 'saved' ? (
        <View testID="cvc-saved" style={styles.statut}>
          <Check size={14} color={colors.vertSurNuit} strokeWidth={3} />
          <Text style={styles.statutTexte}>{t('cvCreateFlow.savedIn')}</Text>
        </View>
      ) : saveState === 'saving' ? (
        <Text testID="cvc-saving" style={styles.statutTexte}>
          {t('cvCreateFlow.saving')}
        </Text>
      ) : null;
  const rScore = (
    <>
            {generated.score_inconnu ? (
              <CarteScoreInconnu testID="cvc-score-inconnu" action={statutEnregistrement} />
            ) : (
              <CarteScore
                testID="cvc-score-carte"
                anneauTestID="cvc-score"
                score={generated.ats_score}
                titre={verdictScore(generated.ats_score)}
                detail={t('cvCreateFlow.atsScore')}
                action={statutEnregistrement}
              />
            )}

            {initial?.ficheDePoste ? (
              <Text testID="cvc-fiche" style={styles.ficheUtilisee}>
                {t('cvImprove.ficheUtilisee')}
              </Text>
            ) : null}

            {editState === 'saved' ? (
              <View testID="cvc-edit-saved" style={styles.statutClair}>
                <Check size={14} color={colors.success} strokeWidth={3} />
                <Text style={styles.saved}>{t('cvCreateFlow.editsSaved')}</Text>
              </View>
            ) : null}

            {saveState === 'error' ? (
              <View testID="cvc-save-error" style={styles.saveErrorBox}>
                <Text style={styles.saveErrorText}>{t('cvCreateFlow.saveFailed')}</Text>
                <Button
                  title={t('cvCreateFlow.retrySave')}
                  variant="secondary"
                  onPress={() => persist(generated)}
                  testID="cvc-retry-save"
                />
              </View>
            ) : null}

                </>
  );
  const rApercu = (
    <>
<CvPreview
              cv={generated}
              name={cvName}
              title={answers.role}
              email={cvContact.email}
              phone={cvContact.phone}
              address={cvContact.address}
              linkedin={cvContact.linkedin}
              template={template}
              accentColor={accentColor}
              outputLanguage={outputLanguage}
              testID="cvc-preview"
            />
            {ajustement ? (
              <Text testID="cvc-condense" style={styles.condense}>
                {t('cvCreateFlow.condensePourUnePage')}
              </Text>
            ) : null}

                </>
  );
  const rModele = (
    <>
<TemplatePicker
              value={{ template, accentColor }}
              onChange={handleTemplateChange}
              testID="cvc-template-picker"
            />

                </>
  );
  const rActions = (
    <>
<View style={styles.actions}>
              <Pressable
                testID="cvc-edit"
                accessibilityRole="button"
                onPress={startEdit}
                style={[styles.action, styles.actionPlein]}
              >
                <Pencil size={15} color={colors.white} strokeWidth={2.4} />
                <Text style={styles.actionPleinTexte}>{t('cvCreateFlow.edit')}</Text>
              </Pressable>
              {onTestInScore ? (
                <Pressable
                  testID="cvc-noter"
                  accessibilityRole="button"
                  onPress={testInScore}
                  style={[styles.action, styles.actionContour]}
                >
                  <Search size={15} color={colors.electricInk} strokeWidth={2.4} />
                  <Text style={styles.actionContourTexte}>{t('cvCreateFlow.noterCe')}</Text>
                </Pressable>
              ) : null}
            </View>

                </>
  );
  const rQualite = (
    <>
{generated.quality_check ? (
              <QualityCheckCard data={generated.quality_check} testID="cvc-quality" />
            ) : null}

              </>
  );
  const rConseils = (
    <>
{generated.advice.length > 0 ? (
            <View style={styles.adviceBox}>
              <Text style={styles.sectionLabel}>{t('cvCreateFlow.aiAdvice')}</Text>
              {generated.advice.map((tip, index) => (
                <Card key={index} testID={`cvc-advice-${index}`}>
                  <Text style={styles.tip}>{tip}</Text>
                </Card>
              ))}
            </View>
          ) : null}

              </>
  );
  const rOffre = (
    <>
{/* ─── Cibler une offre (optionnel) : recommandations APRÈS génération, le CV n'est pas modifié ─── */}
          <View style={styles.offerBlock}>
            <Text style={styles.sectionLabel}>{t('cvCreateFlow.targetOffer')}</Text>
            <Text style={styles.offerHint}>{t('cvCreateFlow.offerHint')}</Text>
            <TextInput
              testID="cvc-offer-text"
              style={[styles.input, styles.inputMultiline]}
              placeholder={t('cvCreateFlow.offerPlaceholder')}
              placeholderTextColor={colors.grey400}
              multiline
              value={offerText}
              onChangeText={setOfferText}
            />
            <View style={[styles.offerButtons, large && styles.offerButtonsL]}>
              <Button title={t('cvCreateFlow.offerFile')} variant="secondary" onPress={pickOfferFile} testID="cvc-offer-pick" />
              <Button title={t('cvCreateFlow.offerCaptures')} variant="secondary" onPress={addOfferImages} testID="cvc-offer-images" />
            </View>
            {offerFile ? (
              <Text testID="cvc-offer-filename" style={styles.filename}>
                {offerFile.name}
              </Text>
            ) : null}
            {offerImages.length > 0 ? (
              <View testID="cvc-offer-thumbs" style={styles.thumbs}>
                {offerImages.map((img) => (
                  <Image key={img.uri} source={{ uri: img.uri }} style={styles.thumb} />
                ))}
              </View>
            ) : null}

            <Button
              title={t('cvCreateFlow.recommendCta')}
              variant="success"
              loading={recoLoading}
              disabled={!hasOffer}
              onPress={getRecommendations}
              testID="cvc-offer-reco"
            />
            {recoError ? (
              <Text testID="cvc-offer-error" style={styles.error}>
                {recoError}
              </Text>
            ) : null}

            {recommendations ? (
              <View testID="cvc-recommendations" style={styles.adviceBox}>
                <Text style={styles.sectionLabel}>{t('cvCreateFlow.recommendSection')}</Text>
                {recommendations.map((reco, index) => (
                  <Card key={index} testID={`cvc-reco-${index}`}>
                    <Text style={styles.tip}>{reco}</Text>
                  </Card>
                ))}
                {onTestInScore ? (
                  <Button
                    title={t('cvCreateFlow.testInScore')}
                    withArrow
                    onPress={testInScore}
                    testID="cvc-test-in-score"
                  />
                ) : null}
              </View>
            ) : null}

            <FeedbackWidget
              key={resultKey}
              feature="cv_create"
              resultKey={resultKey}
              testID="cvc-feedback"
            />
          </View>
    </>
  );
  const rPied = (
    <>
        <PiedAction testID="cvc-actions" enColonne={large}>
          {/* Quota export → le Paywall s'ouvre (déclenché dans useCvExport), pas de message inline. */}
          {exportError ? (
            <Text testID="download-error" accessibilityRole="alert" style={styles.error}>
              {exportError}
            </Text>
          ) : null}
          <GradientButton
            variante="cta"
            hauteur="lg"
            withArrow
            label={t('cvCreateFlow.download')}
            loading={exporting}
            onPress={() =>
              exportCv(generated, {
                name: cvName,
                title: answers.role,
                ...cvContact,
                template,
                accentColor,
                // Langue du DOCUMENT : le PDF sort dans la langue choisie pour le CV, quelle
                // que soit celle de l'interface.
                outputLanguage,
              })
            }
            testID="cvc-download"
          />
        </PiedAction>
    </>
  );

    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <ScreenHeader variante="carte" title={t('cvCreateFlow.readyTitle')} onBack={onBack} testID="cvc-back" />
        <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          {/* Ordinateur : le CV en grand à gauche (il reste en vue), le score, le téléchargement, les modèles et
              les conseils à droite. Un résultat peut défiler ; le téléphone garde sa pile. */}
          {large ? (
            <ScrollView
              testID="cvc-result-scroll"
              contentContainerStyle={styles.colonnesRes}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.colApercu}>{rApercu}</View>
              <View style={styles.colDetail}>
                {rScore}
                <View style={styles.piedRes}>{rPied}</View>
                {rActions}
                {rModele}
                {rQualite}
                {rConseils}
                {rOffre}
              </View>
            </ScrollView>
          ) : (
          <ScrollView
            testID="cvc-result-scroll"
            contentContainerStyle={[styles.content, colonneFormulaire]}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
          >
            {rScore}
            {rApercu}
            {rModele}
            {rActions}
            {rQualite}
            {rConseils}
            {rOffre}
          </ScrollView>
          )}
        </KeyboardAvoidingView>

        {large ? null : rPied}
      </SafeAreaView>
    );
  }

  // ───────────── form ─────────────
  const valeurs: CvFormValues = {
    name,
    role: answers.role ?? '',
    contractType,
    extra: answers.extra ?? '',
    outputLanguage,
    education,
    experiences,
    projects,
    skills,
    interests,
    languages,
  };

  // Le composant dit CE QUI A CHANGÉ ; l'écran garde chaque saisie dans son propre état, ce qui
  // fait que revenir à l'écran précédent ne perd rien.
  const changerValeurs = (patch: Partial<CvFormValues>) => {
    if (patch.name !== undefined) setName(patch.name);
    if (patch.role !== undefined || patch.extra !== undefined) {
      setAnswers((prev) => ({
        ...prev,
        ...(patch.role !== undefined ? { role: patch.role } : {}),
        ...(patch.extra !== undefined ? { extra: patch.extra } : {}),
      }));
    }
    if (patch.contractType !== undefined) {
      contratTouche.current = true;
      setContractType(patch.contractType);
    }
    if (patch.outputLanguage !== undefined) setOutputLanguage(patch.outputLanguage);
    if (patch.education !== undefined) setEducation(patch.education);
    if (patch.experiences !== undefined) setExperiences(patch.experiences);
    if (patch.projects !== undefined) setProjects(patch.projects);
    if (patch.skills !== undefined) setSkills(patch.skills);
    if (patch.interests !== undefined) setInterests(patch.interests);
    if (patch.languages !== undefined) setLanguages(patch.languages);
  };

  return (
    <CvCreateForm
      etape={step}
      valeurs={valeurs}
      onChange={changerValeurs}
      nomDuProfil={profileName || profile?.full_name?.trim() || ''}
      canGenerate={canGenerate}
      loading={loading}
      error={error}
      onBack={step > 0 ? () => setStep(step - 1) : onBack}
      onNext={() => setStep((s) => Math.min(s + 1, ETAPES_CV - 1))}
      onGenerate={generate}
      onImportCv={onImportCv}
    />
  );
}

const styles = StyleSheet.create({
  colonnesRes: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xl,
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    padding: spacing.xxl + spacing.xl,
  },
  // Le CV reste à l'écran pendant que la colonne de droite défile (web seulement : `sticky`).
  colApercu: { flex: 1.1, minWidth: 0, ...({ position: 'sticky', top: spacing.lg } as unknown as ViewStyle) },
  colDetail: { flex: 1, minWidth: 0, gap: spacing.md },
  piedRes: { marginVertical: spacing.xs },
  container: { flex: 1, backgroundColor: colors.grey50 },
  flex: { flex: 1 },
  content: { padding: screenPadding, gap: spacing.md },
  input: {
    minHeight: 48,
    borderWidth: 1,
    borderColor: colors.grey200,
    borderRadius: radius.md,
    padding: spacing.md,
    fontSize: typography.body.size,
    color: colors.grey900,
    backgroundColor: colors.white,
  },
  inputMultiline: { minHeight: 110, textAlignVertical: 'top' },
  quota: { color: colors.warning, fontSize: typography.bodyMd.size, fontWeight: typography.bodyMd.weight },
  error: { color: colors.error, fontSize: typography.small.size },
  condense: { fontSize: typography.caption.size, color: colors.grey500, textAlign: 'center', marginTop: spacing.xs },
  // ── phase generating : plein écran « premium » sombre ──
  genContainer: { flex: 1, backgroundColor: colors.blueNight },
  genContent: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: spacing.xl, gap: spacing.md },
  glow: {
    marginBottom: spacing.lg,
    borderRadius: radius.pill,
    // halo vert autour du cercle
    shadowColor: colors.green,
    shadowOpacity: 0.6,
    shadowRadius: 28,
    shadowOffset: { width: 0, height: 0 },
    elevation: 16,
  },
  circle: {
    width: 96,
    height: 96,
    borderRadius: radius.pill,
    backgroundColor: colors.green,
    alignItems: 'center',
    justifyContent: 'center',
  },
  circleIcon: { fontSize: 40, color: colors.white },
  genTitle: { fontSize: typography.h1.size, fontWeight: typography.h1.weight, color: colors.white, textAlign: 'center' },
  genSubtitle: { fontSize: typography.body.size, color: colors.grey400, textAlign: 'center', marginBottom: spacing.xl },
  checklist: { gap: spacing.lg, alignSelf: 'stretch', paddingHorizontal: spacing.lg },
  checkRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  pastille: {
    width: 26,
    height: 26,
    borderRadius: radius.pill,
    borderWidth: 2,
    borderColor: colors.grey700,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pastilleDone: { backgroundColor: colors.green, borderColor: colors.green },
  pastilleCheck: { color: colors.white, fontSize: typography.small.size, fontWeight: typography.bodyMd.weight },
  checkLabel: { fontSize: typography.bodyMd.size, color: colors.white },
  ficheUtilisee: { fontSize: typography.small.size, color: colors.grey500, textAlign: 'center' },
  saved: { color: colors.success, fontSize: typography.bodyMd.size, fontWeight: typography.bodyMd.weight },
  erreurEdition: { color: colors.error, fontSize: typography.small.size, paddingHorizontal: screenPadding, paddingBottom: spacing.sm },
  statut: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs + 2 },
  statutTexte: { fontSize: typography.small.size, color: colors.blancAtenue },
  statutClair: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs + 2 },
  actions: { flexDirection: 'row', gap: spacing.sm },
  action: {
    flex: 1,
    minHeight: formes.cible,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm - 1,
    paddingHorizontal: spacing.md,
    borderRadius: formes.rayon.xl,
  },
  actionPlein: { backgroundColor: colors.night },
  actionPleinTexte: { fontSize: 14, fontWeight: '800', color: colors.white },
  actionContour: { borderWidth: 1.5, borderColor: colors.bordureClaire, backgroundColor: colors.white },
  actionContourTexte: { fontSize: 14, fontWeight: '800', color: colors.electricInk },
  saveErrorBox: { gap: spacing.sm },
  saveErrorText: { color: colors.error, fontSize: typography.small.size },
  adviceBox: { gap: spacing.sm },
  sectionLabel: {
    fontSize: typography.caption.size,
    fontWeight: typography.caption.weight,
    letterSpacing: typography.caption.letterSpacing,
    color: colors.grey500,
  },
  tip: { fontSize: typography.body.size, color: colors.grey700 },
  offerBlock: { gap: spacing.sm, padding: spacing.lg, borderRadius: radius.lg, backgroundColor: colors.white, borderWidth: 1, borderColor: colors.grey200 },
  offerHint: { fontSize: typography.small.size, color: colors.grey500 },
  offerButtons: { flexDirection: 'row', gap: spacing.sm },
  offerButtonsL: { flexDirection: 'column' },
  filename: { fontSize: typography.bodyMd.size, color: colors.bluePrimary },
  thumbs: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  thumb: { width: 56, height: 56, borderRadius: radius.sm, backgroundColor: colors.grey200 },
});
